import moment from 'moment';
import React from 'react';
import {
  MAX_DAYS_PER_YEAR,
  MAX_MINUTES_PER_DAY,
} from '../../../controlUnit/api/util/constants';
import { ComposedParametersValue } from '../../selectors';
import { getPolyglot } from '../../../../i18n';
import {
  getDecimals,
  getPreferredUnit,
  getValueWithPreferredUnitAndDecimals,
} from '../../../controlUnit/api/util/sensor/formatValues';
import {
  getTemperature,
  TemperatureUnit,
} from '../../../../customized/fresh-watch/theme/utils/temperatureUtils';
import _ from 'lodash';

export function getValuesFromBits<T extends { id: string }>(
  bits: T[],
  value?: string
) {
  return (
    bits?.filter((item) => (2 ** parseInt(item.id)) & parseInt(value || '0')) ||
    []
  );
}
export function getValueFromBits(bits: { id: string }[]) {
  return bits.reduce((acc, item) => acc + 2 ** parseInt(item.id), 0).toString();
}

export function getDateTimeMidnight() {
  const year = moment().year();
  const month = moment().month();
  const day = moment().date();
  const dateTimeMidnight = new Date(year, month, day, 0, 0, 0, 0);
  return dateTimeMidnight;
}
export function getDateTimeValue(numberValue: number) {
  const dateTimeMidnight = getDateTimeMidnight();
  const dateTimeValue = moment(dateTimeMidnight).add(
    numberValue % MAX_MINUTES_PER_DAY,
    'minutes'
  );
  return dateTimeValue;
}
export function getDate1stJan() {
  const year = moment().year();
  const date1stJan = new Date(year, 0, 1, 0, 0, 0, 0);
  return date1stJan;
}
export function getDateValue(numberValue: number) {
  const date1stJan = getDate1stJan();
  const dateValue = moment(date1stJan).add(
    numberValue % MAX_DAYS_PER_YEAR,
    'days'
  );
  return dateValue;
}

export function getParameterValue(
  parameter: ComposedParametersValue,
  preferredTemperatureUnit: string
): React.ReactNode {
  if (parameter.value === undefined) return '--/--';
  if (parameter.selections) {
    const selection = parameter.selections.find(
      (item) => item.id === parameter.value
    );
    if (selection) return <p>{`${selection.id}: ${selection.text}`}</p>;
  }
  if (parameter.bits) {
    const bits = getValuesFromBits(parameter.bits, parameter.value);

    if (bits)
      return bits.map((item) => (
        <div key={item.id}>{`+${2 ** parseInt(item.id)}: ${item.text}`}</div>
      ));
  }
  const numberValue = parseFloat(parameter.value);

  if (parameter.type === 'TIME') {
    const dateTimeValue = getDateTimeValue(numberValue);
    return dateTimeValue.format('HH:mm');
  }
  if (parameter.type === 'DATE') {
    const dateValue = getDateValue(numberValue);
    return dateValue.format('L');
  }

  const { strValue: value } = getValueWithPreferredUnitAndDecimals(
    numberValue,
    parameter.unitString ?? parameter.unit,
    parameter.decimals,
    preferredTemperatureUnit
  );

  return value ?? parameter.value ?? '--/--';
}

export function getParameterTitle(
  parameter: ComposedParametersValue,
  preferredTemperatureUnit: string
): string {
  const polyglot = getPolyglot();
  let title = parameter.name;
  if (parameter.titleTranslation) title += `: ${parameter.titleTranslation}`;
  if (parameter.selections || parameter.bits) return title;

  const tempUnit = getPreferredUnit(
    parameter.unitString ?? parameter.unit,
    preferredTemperatureUnit
  ) as TemperatureUnit;
  const decimals = getDecimals(parameter.decimals, preferredTemperatureUnit);
  const minValue = _.ceil(getTemperature(parameter.min, tempUnit), decimals);
  const maxValue = _.floor(getTemperature(parameter.max, tempUnit), decimals);

  const minMaxValuesLabel =
    (minValue || minValue === 0) &&
    (maxValue || maxValue === 0) &&
    // Boolean case
    !(minValue === 0 && maxValue === 1) &&
    minValue !== maxValue
      ? `[${minValue}, ${maxValue}]`
      : undefined;

  if (minMaxValuesLabel) title += ` ${minMaxValuesLabel}`;
  if (parameter.unitString ?? parameter.unit) title += ` ${tempUnit}`;
  if (!parameter.changeable)
    title += ` (${polyglot.t('device.parameters.non_configurable_parameter')})`;

  return title;
}
